/**
 * Marketing attribution capture (S3 — Loyalty & Marketing Revamp, Block 3).
 *
 * On landing this module reads UTM params, ad click-ids, an optional
 * `campaign_id`/`cid` and an operator-configurable set of `custom_params`
 * from the URL, records a "touch", and persists the touch history so it can
 * be attached to the eventual order POST.
 *
 * Storage:
 *   - `tappya.attribution.touches`  → array of touches, most-recent-first,
 *      capped at MAX_TOUCHES, with a 90-day TTL (pruned on read).
 *   - `tappya.attribution.session`  → the single latest touch.
 *
 * Consent:
 *   The app's analytics consent gates persistence. When analytics consent is
 *   OFF we keep everything in-memory only (session-scoped) and never write to
 *   localStorage; the data is dropped when the tab/session ends. Toggle via
 *   `setAnalyticsConsent(optedIn)`.
 *
 * No PII is ever captured here — only marketing metadata from the URL.
 *
 * The order POST body field `attribution` is camelCase and must match the
 * order-service contract exactly:
 *
 *   attribution: {
 *     firstTouch: Touch | null,   // oldest stored touch
 *     lastTouch:  Touch | null,   // newest stored touch
 *     clickIds:   { fbclid?, gclid? },
 *     touchHistory: Touch[],      // most-recent-first, capped 10
 *   }
 *
 * (`waterfallMethod` is computed server-side and is NOT sent from here.)
 */

const TOUCHES_KEY = "tappya.attribution.touches";
const SESSION_KEY = "tappya.attribution.session";

const MAX_TOUCHES = 10;
const TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

/**
 * Operator custom-param whitelist.
 *
 * Defaults to the empty set: only standard UTM / click-id / campaign params
 * are captured out of the box. To capture additional operator-declared query
 * params (e.g. per-tenant tracking keys like `aff` or `ref`), call
 * `configureCustomParams(["aff", "ref"])` once at bootstrap — typically from
 * tenant config. Whitelisting (rather than capturing every stray param) keeps
 * accidental PII out of attribution.
 */
let CUSTOM_PARAM_WHITELIST: string[] = [];

/** Standard UTM dimensions we always read. */
const UTM_KEYS = [
  "source",
  "medium",
  "campaign",
  "content",
  "term",
] as const;

export interface Utm {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
}

export interface Touch {
  campaignId?: string;
  utm: Utm;
  customParams: Record<string, string>;
  /** ISO-8601 timestamp of when the touch was captured. */
  ts: string;
}

export interface OrderAttribution {
  firstTouch: Touch | null;
  lastTouch: Touch | null;
  clickIds: { fbclid?: string; gclid?: string };
  customParams: Record<string, string>;
  touchHistory: Touch[];
}

/** Persisted envelope wrapping a touch with its capture time for TTL pruning. */
interface StoredTouch {
  touch: Touch;
  storedAt: number; // epoch ms
}

// ---------------------------------------------------------------------------
// Consent + in-memory session fallback
// ---------------------------------------------------------------------------

/**
 * Consent defaults to OFF (opt-in). Until the host app calls
 * `setAnalyticsConsent(true)`, captured touches live only in memory and are
 * never written to localStorage.
 */
let analyticsConsent = false;

/** Session-scoped store used when consent is OFF (most-recent-first). */
let sessionTouches: StoredTouch[] = [];

/** Click-ids are kept separately so they survive even without a full touch. */
let clickIds: { fbclid?: string; gclid?: string } = {};

/**
 * Declare consent for analytics-grade persistence.
 *
 * - `true`  → future touches are persisted to localStorage. Any touches
 *             captured in-memory before consent are flushed to storage.
 * - `false` → stop persisting; clear anything already on disk and fall back
 *             to in-memory/session-only capture.
 */
export function setAnalyticsConsent(optedIn: boolean): void {
  const was = analyticsConsent;
  analyticsConsent = optedIn;
  if (optedIn && !was) {
    // Promote whatever we gathered in-memory to durable storage.
    if (sessionTouches.length) {
      writeStored(sessionTouches);
      writeSession(sessionTouches[0]?.touch ?? null);
    }
  } else if (!optedIn && was) {
    // Consent withdrawn — purge disk, keep current session state in memory.
    const current = readStored();
    sessionTouches = current.length ? current : sessionTouches;
    removeKey(TOUCHES_KEY);
    removeKey(SESSION_KEY);
  }
}

export function getAnalyticsConsent(): boolean {
  return analyticsConsent;
}

/** Extend the operator custom-param whitelist (idempotent, de-duped). */
export function configureCustomParams(keys: string[]): void {
  const set = new Set([...CUSTOM_PARAM_WHITELIST, ...keys]);
  CUSTOM_PARAM_WHITELIST = Array.from(set);
}

// ---------------------------------------------------------------------------
// Storage helpers (defensive — localStorage may be unavailable / full)
// ---------------------------------------------------------------------------

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function removeKey(key: string): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readStored(): StoredTouch[] {
  const ls = safeLocalStorage();
  if (!ls) return [];
  try {
    const raw = ls.getItem(TOUCHES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredTouch[];
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    // Prune expired touches on read.
    const fresh = parsed.filter(
      (s) =>
        s &&
        typeof s.storedAt === "number" &&
        now - s.storedAt < TTL_MS &&
        s.touch,
    );
    if (fresh.length !== parsed.length) writeStored(fresh);
    return fresh.slice(0, MAX_TOUCHES);
  } catch {
    return [];
  }
}

function writeStored(touches: StoredTouch[]): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(TOUCHES_KEY, JSON.stringify(touches.slice(0, MAX_TOUCHES)));
  } catch {
    /* quota / privacy mode — silently degrade */
  }
}

function writeSession(touch: Touch | null): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    if (touch) ls.setItem(SESSION_KEY, JSON.stringify(touch));
    else ls.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/** Returns the active touch list (disk when consented, memory otherwise). */
function currentTouches(): StoredTouch[] {
  return analyticsConsent ? readStored() : sessionTouches;
}

// ---------------------------------------------------------------------------
// URL parsing
// ---------------------------------------------------------------------------

function firstNonEmpty(...vals: (string | null | undefined)[]): string | undefined {
  for (const v of vals) {
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return undefined;
}

interface ParsedUrl {
  touch: Touch;
  hasTrackedSignal: boolean;
  fbclid?: string;
  gclid?: string;
}

/**
 * Parse a URL query string into a Touch. Returns `hasTrackedSignal` so callers
 * (and the consent banner) can tell whether the visitor actually arrived via a
 * tracked link rather than a bare visit.
 */
export function parseUrl(search?: string): ParsedUrl {
  const qs =
    search ??
    (typeof window !== "undefined" ? window.location.search : "");
  const params = new URLSearchParams(qs || "");

  const utm: Utm = {};
  for (const k of UTM_KEYS) {
    const val = params.get(`utm_${k}`);
    if (val && val.trim()) utm[k] = val.trim();
  }

  const fbclid = firstNonEmpty(params.get("fbclid"));
  const gclid = firstNonEmpty(params.get("gclid"));

  const campaignId = firstNonEmpty(
    params.get("campaign_id"),
    params.get("cid"),
  );

  const customParams: Record<string, string> = {};
  for (const key of CUSTOM_PARAM_WHITELIST) {
    const val = params.get(key);
    if (val && val.trim()) customParams[key] = val.trim();
  }

  const hasTrackedSignal =
    Object.keys(utm).length > 0 ||
    !!fbclid ||
    !!gclid ||
    !!campaignId ||
    Object.keys(customParams).length > 0;

  const touch: Touch = {
    ...(campaignId ? { campaignId } : {}),
    utm,
    customParams,
    ts: new Date().toISOString(),
  };

  return { touch, hasTrackedSignal, fbclid, gclid };
}

/**
 * Whether the current (or supplied) URL carries any tracked-link signal.
 * Used to decide whether to show the consent banner.
 */
export function arrivedViaTrackedLink(search?: string): boolean {
  return parseUrl(search).hasTrackedSignal;
}

// ---------------------------------------------------------------------------
// Capture
// ---------------------------------------------------------------------------

/**
 * Entry point — call once on landing/bootstrap. Reads the URL, and if there's
 * a tracked-link signal, records a new touch (prepended, most-recent-first,
 * capped at MAX_TOUCHES). Click-ids are always merged into the running set.
 *
 * No-op for the touch list when the URL has no tracked signal (a plain visit
 * shouldn't pollute the history with empty touches).
 */
export function captureFromUrl(search?: string): void {
  const parsed = parseUrl(search);

  // Merge click-ids (latest wins) regardless — they ride alongside touches.
  if (parsed.fbclid) clickIds.fbclid = parsed.fbclid;
  if (parsed.gclid) clickIds.gclid = parsed.gclid;

  if (!parsed.hasTrackedSignal) return;

  const entry: StoredTouch = { touch: parsed.touch, storedAt: Date.now() };
  const next = [entry, ...currentTouches()].slice(0, MAX_TOUCHES);

  if (analyticsConsent) {
    writeStored(next);
    writeSession(entry.touch);
  } else {
    sessionTouches = next;
  }
}

// ---------------------------------------------------------------------------
// Read for order POST
// ---------------------------------------------------------------------------

/**
 * Build the `attribution` block to attach to the order-creation POST body.
 * Matches the order-service camelCase contract exactly.
 *
 * - firstTouch = oldest stored touch (history is most-recent-first → last).
 * - lastTouch  = newest stored touch (first element).
 * - customParams = the newest touch's custom params (most relevant set).
 */
export function getAttributionForOrder(): OrderAttribution {
  const stored = currentTouches();
  const history = stored.map((s) => s.touch);

  const lastTouch = history.length ? history[0] : null;
  const firstTouch = history.length ? history[history.length - 1] : null;

  return {
    firstTouch,
    lastTouch,
    clickIds: { ...clickIds },
    customParams: lastTouch ? { ...lastTouch.customParams } : {},
    touchHistory: history,
  };
}

/**
 * True when there's any attribution worth sending (a touch or a click-id).
 * Callers can skip attaching an empty block to keep order payloads lean.
 */
export function hasAttribution(): boolean {
  return currentTouches().length > 0 || !!clickIds.fbclid || !!clickIds.gclid;
}

/** Test/reset helper — clears in-memory state and persisted keys. */
export function __resetAttribution(): void {
  sessionTouches = [];
  clickIds = {};
  analyticsConsent = false;
  CUSTOM_PARAM_WHITELIST = [];
  removeKey(TOUCHES_KEY);
  removeKey(SESSION_KEY);
}
